import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { extractApiErrorMessage } from '@/lib/api-client';
import type { InvoiceRow } from '../types';
import { buildSavePayload } from './downpayment-invoice-payload';
import { useInvoiceCreatePdf } from './invoice-create-pdf-context';
import { saveInvoiceWithPdf } from './save-invoice-with-pdf';
import type { AdvanceSubmitValues } from './downpayment-invoice-schema';

export interface UseSaveAdvanceMutationArgs {
  readonly side: 'buy' | 'sell';
  readonly orderId: string;
  readonly existingInvoice: InvoiceRow | null;
  readonly aiExtractionId?: string;
  // Reads the create picker's coverage at submit time (latest edit, not a
  // stale closure). Undefined = no picker, the server splits quantity-ratio.
  readonly getCoverage?: () => readonly { bl_order_id: string; amount: number }[] | undefined;
  readonly onSaved: () => void;
  readonly onOpenChange: (open: boolean) => void;
}

export function useSaveAdvanceMutation(args: UseSaveAdvanceMutationArgs) {
  const { side, orderId, existingInvoice, aiExtractionId, getCoverage, onSaved, onOpenChange } =
    args;
  const qc = useQueryClient();
  // The dialog's right-pane PDF: uploaded against the new invoice after a
  // create, so attaching the supplier document and entering the fields stay
  // one surface (same plumbing as the trade dialogs).
  const { file: pdfFile } = useInvoiceCreatePdf();
  return useMutation<unknown, Error, AdvanceSubmitValues>({
    mutationFn: (values) => {
      const coverage = getCoverage?.();
      const payload = buildSavePayload(values, {
        side,
        orderId,
        existingInvoice,
        ...(aiExtractionId !== undefined ? { aiExtractionId } : {}),
        ...(coverage !== undefined ? { coverage } : {}),
      });
      return saveInvoiceWithPdf(existingInvoice, payload, pdfFile);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['order-advance-invoices', orderId] });
      void qc.invalidateQueries({ queryKey: ['order-invoices', orderId] });
      // Advance rows also render in the BL invoice listing (fed by
      // bl-order-detail); refresh it so a row edit reflects immediately.
      void qc.invalidateQueries({ queryKey: ['bl-order-detail'] });
      toast.success(
        existingInvoice !== null ? 'Downpayment invoice updated.' : 'Downpayment invoice created.',
      );
      onSaved();
      onOpenChange(false);
    },
    onError: (err) => {
      toast.error(extractApiErrorMessage(err, 'Failed to save downpayment invoice.'));
    },
  });
}
